import React, { useState, useEffect } from "react";
import { Modal } from "reactstrap";
import { Button } from "shared";
import "./index.scss";
import moment from "moment";
import t from "utils/Lang";
import ApiCaller from "api/apiCaller";
import ContractInfo from "./ContractInfo";
import ContractTax from "./ContractTax";
import PaymentTerm from "./PaymentTerm";
import OtherFee from "./OtherFee";
import Question from "./Question";
import api from "api";

type ModalCreateContractProps = {
  isOpen: boolean;
  toggle: () => void;
  onApply: (data: any, paymentTerms: Array<any>, taxData: any) => void;
  onClosed: () => void;
  contractId?: number;
  siteAddress: string;
  siteId?: number;
  contracts?: Array<any>;
};

const ModalCreateContract = ({
  isOpen,
  toggle,
  onApply,
  onClosed,
  contractId,
  siteAddress,
  siteId,
  contracts,
}: ModalCreateContractProps) => {
  const tabs = [
    { label: t().site_contract_modal__contract_info, id: 0 },
    { label: t().contract_tax, id: 1 },
    { label: t().site_contract_modal__payment_term, id: 2 },
    { label: t().other_fee, id: 3 },
    { label: t().question, id: 4 },
  ];
  const [linkContracts, setLinkContracts] = useState<Array<any>>(
    contracts || []
  );
  const [errorTabs, setErrorTabs] = useState<Array<boolean>>(
    tabs.map(() => false)
  );
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [depositTypes, setDepositTypes] = useState<Array<any>>([]);
  const [taxTypes, setTaxTypes] = useState<Array<any>>([]);
  const [purposeTypes, setPurposeTypes] = useState<Array<any>>([]);
  const [contractTypes, setContractTypes] = useState<Array<any>>([]);
  const [periodTypes, setPeriodTypes] = useState<Array<any>>([]);
  const [paymentCalculationTypes, setPaymentCalculationTypes] = useState<
    Array<any>
  >([]);
  useEffect(() => {
    if (siteId) {
      getLinkContracts();
    }
  }, [siteId]);
  useEffect(() => {
    getContractTypes();
    getTaxTypes();
    getPurposeTypes();
    getDepositTypes();
    getPaymentCalculationTypes();
    getPeriodTypes();
  }, []);
  const getLinkContracts = async () => {
    const res = await api.getSiteContract(siteId);
    if (res.success) {
      setLinkContracts(res?.result?.contracts || []);
    }
  };
  const getPeriodTypes = async () => {
    const res = await ApiCaller.get("contract_payment_period_type/");
    if (res.result) {
      setPeriodTypes(res.result);
    }
  };
  const getPaymentCalculationTypes = async () => {
    const res = await ApiCaller.get("contract_payment_calculation_type/");
    if (res.result) {
      setPaymentCalculationTypes(res.result);
    }
  };
  const getDepositTypes = async () => {
    const depositTypeRes = await ApiCaller.get("contract_deposit_type/");
    if (depositTypeRes.result) {
      setDepositTypes(depositTypeRes.result);
    }
  };
  const getPurposeTypes = async () => {
    const purposeTypeRes = await ApiCaller.get("contract_purpose/");
    if (purposeTypeRes.result) {
      setPurposeTypes(purposeTypeRes.result);
    }
  };
  const getTaxTypes = async () => {
    const taxTypeRes = await ApiCaller.get("contract_tax_type");
    if (taxTypeRes.contract_tax_types) {
      setTaxTypes(taxTypeRes.contract_tax_types);
    }
  };
  const getContractTypes = async () => {
    const contractTypeRes = await ApiCaller.get("contract_type/");
    if (contractTypeRes.result) {
      setContractTypes(contractTypeRes.result);
    }
  };
  const initialState = {
    available_from: moment(new Date()).format("YYYY-MM-DD"),
    available_to: moment(new Date()).format("YYYY-MM-DD"),
    contract_valid_from: moment(new Date()).format("YYYY-MM-DD"),
    contract_valid_to: moment(new Date()).format("YYYY-MM-DD"),
    contract_signed_date: moment(new Date()).format("YYYY-MM-DD"),
  };
  const initialPaymentTerms = [
    {
      from_date: moment(new Date()).format("YYYY-MM-DD"),
      to_date: moment(new Date()).format("YYYY-MM-DD"),
      rental_fee: null,
      increase_fee_rate: null,
      contract_payment_calculation_type_id: null,
      revenue_share_percentage: null,
      payment_file: null,
    },
  ];
  const initialTaxData = {
    contract_tax_type_id: null,
    tax_percentage: 0,
    id: null,
  };
  const [errorData, setErrorData] = useState<any>({});
  const [data, setData] = useState<any>(initialState);
  const [taxData, setTaxData] = useState(initialTaxData);
  const [paymentTerms, setPaymentTerms] = useState<
    Array<{
      id?: number;
      from_date: string;
      to_date: string;
      rental_fee: number | null;
      increase_fee_rate: number | null;
      contract_payment_calculation_type_id: number | null;
      revenue_share_percentage: number | null;
      payment_file?: any;
    }>
  >(initialPaymentTerms);
  const getContractData = async () => {
    const res = await ApiCaller.get(`contract/${contractId}`);
    fillData(res);
  };
  const fillData = (res: any, isAutoFillFromParent?: boolean) => {
    if (res.status === "ok") {
      const contract_file = res.result.attachments.find(
        (el: any) => el.attachment_content_relation_type === "contract_file"
      );
      const paymentFiles = res.result.attachments.filter(
        (el: any) => el.attachment_content_relation_type !== "contract_file"
      );
      setData((current: any) => {
        let newData = res.result
          ? { ...res.result, contract_file }
          : initialState;
        if (isAutoFillFromParent) {
          newData.name = current.name;
          newData.contract_type_id = current.contract_type_id;
          newData.parent_contract_id = current.parent_contract_id;
          newData.id = null;
        }
        return newData;
      });
      const tax = res.result.contract_taxes?.[0];
      setTaxData(
        tax
          ? {
              id: tax?.id,
              contract_tax_type_id: tax?.contract_tax_type_id,
              tax_percentage: (tax?.tax_percentage || 0) * 100,
            }
          : initialTaxData
      );
      setPaymentTerms(
        res.result?.payment_terms?.length > 0
          ? res.result?.payment_terms.map((el: any, index: number) => {
              if (el.revenue_share_percentage) {
                el.revenue_share_percentage = 100 * el.revenue_share_percentage;
              }
              el.payment_file = paymentFiles.find((f: any) =>
                f.attachment_content_relation_type.includes(index)
              );
              return el;
            })
          : initialPaymentTerms
      );
    }
  };
  useEffect(() => {
    if (contractId) {
      getContractData();
    } else {
      setData(initialState);
      setTaxData(initialTaxData);
      setPaymentTerms(initialPaymentTerms);
    }
  }, [contractId]);
  const validateContractInfo = () => {
    setErrorData({
      ...errorData,
      name: !data.name ? t().cannot_empty : "",
      contract_type_id: !data.contract_type_id ? t().cannot_empty : "",
      parent_contract_id:
        data?.contract_type_id === 2 && !data.parent_contract_id
          ? t().cannot_empty
          : "",
      contract_code: !data.contract_code ? t().cannot_empty : "",
    });
    const errTabs = errorTabs;
    let valid = true;
    if (
      !data.name ||
      !data.contract_type_id ||
      !data.contract_code ||
      (data?.contract_type_id === 2 && !data.parent_contract_id)
    ) {
      errTabs[0] = true;
      valid = false;
    } else {
      errTabs[0] = false;
    }
    setErrorTabs(errTabs);
    return valid;
  };
  const validateContractTax = () => {
    setErrorData({
      ...errorData,
      tax_percentage: !taxData.tax_percentage ? t().cannot_empty : "",
      contract_tax_type_id: !taxData.contract_tax_type_id
        ? t().cannot_empty
        : "",
    });
    const errTabs = errorTabs;
    let valid = true;
    if (!taxData.tax_percentage || !taxData.contract_tax_type_id) {
      valid = false;
      errTabs[1] = true;
    } else {
      errTabs[1] = false;
    }
    setErrorTabs(errTabs);
    return valid;
  };
  const validateContractPaymentTerm = () => {
    const firstTerm = paymentTerms?.[0];
    const isAbsolute = firstTerm.contract_payment_calculation_type_id
      ? paymentCalculationTypes.find(
          (el) => el.id === firstTerm.contract_payment_calculation_type_id
        )?.code !== "store_revenue"
      : false;
    const byRevenue = firstTerm.contract_payment_calculation_type_id
      ? paymentCalculationTypes.find(
          (el) => el.id === firstTerm.contract_payment_calculation_type_id
        )?.code !== "fixed_rental_fee"
      : false;
    setErrorData({
      ...errorData,
      contract_payment_calculation_type_id:
        !firstTerm?.contract_payment_calculation_type_id
          ? t().cannot_empty
          : "",
      rental_fee: isAbsolute && !firstTerm?.rental_fee ? t().cannot_empty : "",
      revenue_share_percentage:
        byRevenue && !firstTerm?.revenue_share_percentage
          ? t().cannot_empty
          : "",
    });
    const errTabs = errorTabs;
    let valid = true;
    if (
      !firstTerm?.contract_payment_calculation_type_id ||
      (isAbsolute && !firstTerm?.rental_fee) ||
      (byRevenue && !firstTerm?.revenue_share_percentage)
    ) {
      errTabs[2] = true;
      valid = false;
    } else {
      errTabs[2] = false;
    }
    setErrorTabs(errTabs);
    return valid;
  };
  const validateContractOtherFee = () => {
    setErrorData({
      ...errorData,
      deposit_fee: !data.deposit_fee ? t().cannot_empty : "",
      contract_deposit_attribute_to_month:
        data.contract_deposit_type_id &&
        !data.contract_deposit_attribute_to_month
          ? t().cannot_empty
          : "",
    });
    const errTabs = errorTabs;
    let valid = true;
    console.log('Get here...');
    if (
      !data.deposit_fee ||
      (data.contract_deposit_type_id &&
        !data.contract_deposit_attribute_to_month)
    ) {
      errTabs[3] = true;
      valid = false;
    } else {
      errTabs[3] = false;
    }
    setErrorTabs(errTabs);
    return valid;
  };
  const validateField = () => {
    if (selectedIndex === 0) {
      return validateContractInfo();
    }
    if (selectedIndex === 1) {
      return validateContractInfo() && validateContractTax();
    }
    if (selectedIndex === 2) {
      return (
        validateContractInfo() &&
        validateContractTax()
        // validateContractPaymentTerm()
      );
    }
    return (
      validateContractInfo() &&
      validateContractTax() &&
      validateContractPaymentTerm()
      // validateContractOtherFee()
    );
  };
  const apply = (
    action: "create" | "edit" | "delete",
    isClose: boolean = false
  ) => {
    const valid = validateField();
    if (!valid) {
      alert("Có lỗi xảy ra");
      return;
    }
    onApply({ ...data, is_can_edit: !isClose, action }, paymentTerms, taxData);
  };
  const updatePaymentTerm =
    (index: number) => (attribute: string, value: any) => {
      const newPaymentTerms = [...paymentTerms];
      newPaymentTerms[index] = {
        ...newPaymentTerms[index],
        [attribute]: value?.value || value,
      };
      if (attribute === "increase_fee_rate_with_tax") {
        newPaymentTerms[index].rental_fee =
          value / ((taxData.tax_percentage || 0) / 100 + 1);
      }
      if (index > 0) {
        const rentalFee = newPaymentTerms[index].rental_fee;
        const increaseFee = newPaymentTerms[index].increase_fee_rate;
        const prevRentalFee = newPaymentTerms[index - 1].rental_fee;
        if (prevRentalFee) {
          if (
            (attribute === "rental_fee" ||
              attribute === "increase_fee_rate_with_tax") &&
            rentalFee
          ) {
            newPaymentTerms[index].increase_fee_rate = Math.round(
              ((rentalFee - prevRentalFee) * 100) / prevRentalFee
            );
          }
          if (attribute === "increase_fee_rate" && increaseFee) {
            newPaymentTerms[index].rental_fee = Math.round(
              prevRentalFee * (1 + increaseFee / 100)
            );
          }
        }
      }
      setPaymentTerms(newPaymentTerms);
    };
  const updateTaxData = (attribute: string, value: any) => {
    let dataUpdate = {};
    if (value?.label) {
      // Check value is object (value from dropdown menu)
      dataUpdate = {
        [attribute]: value?.value,
      };
    } else {
      dataUpdate = { [attribute]: value };
    }
    setTaxData({
      ...taxData,
      ...dataUpdate,
    });
  };
  const fillParentContractId = async (parentContractId: number) => {
    const res = await ApiCaller.get(`contract/${parentContractId}`);
    fillData(res, true);
  };
  const updateAttr = (attribute: string, value: any) => {
    let dataUpdate = {};
    if (value?.label) {
      // Check value is object (value from dropdown menu)
      dataUpdate = {
        [attribute]: value?.value,
      };
    } else {
      dataUpdate = { [attribute]: value };
    }
    setData({
      ...data,
      ...dataUpdate,
    });
    if (attribute === "parent_contract_id") {
      fillParentContractId(value?.value);
    }
  };
  const isViewContract = !!(contractId && data?.is_can_edit === false);
  const fieldIsInvalid = (err: any) => err != null && err !== "";
  const renderFooter = () => {
    if (contractId && data?.is_can_edit !== false) {
      return (
        <div className="create-contract__footer">
          <Button label={t().action__cancel} onClick={toggle} />
          <div style={{ width: 10 }} />
          <Button
            label={t().action__save}
            type="primary"
            onClick={() => apply("edit")}
          />
        </div>
      );
    }
    if (isViewContract) {
      return (
        <div className="create-contract__footer">
          <Button label={t().action__close} onClick={toggle} />
        </div>
      );
    }
    return (
      <div className="create-contract__footer">
        <Button
          label={selectedIndex === 0 ? t().action__cancel : "Back"}
          onClick={() => {
            if (selectedIndex === 0) {
              toggle();
            } else {
              setSelectedIndex(selectedIndex - 1);
            }
          }}
        />
        <div style={{ width: 10 }} />
        <Button
          label={selectedIndex === tabs.length - 1 ? t().action__done : "Next"}
          type="primary"
          onClick={() => {
            const valid = validateField();
            if (!valid) {
              alert("Có lỗi xảy ra");
              return;
            }
            if (selectedIndex === tabs.length - 1) {
              apply("create");
            } else {
              setSelectedIndex(selectedIndex + 1);
            }
          }}
        />
      </div>
    );
  };
  const selectedTabId = tabs[selectedIndex].id;
  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      style={{ padding: 30, maxWidth: 800 }}
      onClosed={() => {
        onClosed();
        setData(initialState);
        setPaymentTerms(initialPaymentTerms);
        setSelectedIndex(0);
      }}
    >
      <div className="create-contract__header">
        <span className="create-contract__header__title">
          {contractId
            ? data?.is_can_edit !== false
              ? t().site_contract_modal__edit
              : t().site_contract_modal__view
            : t().site_contract_modal__title}
        </span>
      </div>
      <div className="create-contract__body-wrapper">
        <div className="contract-tab">
          {tabs.map((el, index) => (
            <div
              className={`tab-item ${
                el.id === selectedTabId ? "selected" : ""
              } ${errorTabs[index] ? "tab-error" : ""}`}
              key={el.id}
              onClick={() => setSelectedIndex(index)}
            >
              <span>{el.label}</span>
            </div>
          ))}
        </div>
        <div className="line-separate" />
        <div className="create-contract__body">
          {selectedTabId === tabs[0].id && (
            <ContractInfo
              siteAddress={siteAddress}
              purposeTypes={purposeTypes}
              contractTypes={contractTypes}
              data={data}
              errorData={errorData}
              fieldIsInvalid={fieldIsInvalid}
              isViewContract={isViewContract}
              updateAttr={updateAttr}
              periodTypes={periodTypes}
              linkContracts={linkContracts}
              internalCode={contractId && data?.store?.internal_code}
            />
          )}
          {selectedTabId === tabs[1].id && (
            <ContractTax
              taxTypes={taxTypes}
              taxData={taxData}
              updateTaxData={updateTaxData}
              errorData={errorData}
              isViewContract={isViewContract}
              fieldIsInvalid={fieldIsInvalid}
            />
          )}
          {selectedTabId === tabs[2].id && (
            <>
              <PaymentTerm
                taxData={taxData}
                updatePaymentTerm={updatePaymentTerm}
                isViewContract={isViewContract}
                fieldIsInvalid={fieldIsInvalid}
                paymentTerms={paymentTerms}
                setPaymentTerms={setPaymentTerms}
                contractId={contractId}
                errorData={errorData}
                paymentCalculationTypes={paymentCalculationTypes}
              />
              {!isViewContract && (
                <Button
                  label={t().add_payment_term}
                  type="primary"
                  style={{
                    width: "100%",
                    justifyContent: "center",
                    marginTop: 10,
                  }}
                  onClick={() => {
                    const p = paymentTerms[paymentTerms.length - 1];
                    let previousFee;
                    let label;
                    if (!p.contract_payment_calculation_type_id) {
                      alert(
                        `${
                          t().site_contract_modal__type_of_rent_calculation
                        } can not be empty`
                      );
                      return;
                    }
                    if (p.contract_payment_calculation_type_id === 1) {
                      previousFee = p.revenue_share_percentage;
                      label = t().rental_fee_percent;
                    } else if (p.contract_payment_calculation_type_id === 2) {
                      previousFee = p.rental_fee;
                      label = t().rental_fee;
                    } else {
                      previousFee = p.rental_fee && p.revenue_share_percentage;
                      label = `${t().rental_fee_percent} & ${t().rental_fee}`;
                    }
                    if (!previousFee) {
                      alert(`${label} can not be empty`);
                      return;
                    }
                    setPaymentTerms([
                      ...paymentTerms,
                      {
                        from_date: moment(new Date()).format("YYYY-MM-DD"),
                        to_date: moment(new Date()).format("YYYY-MM-DD"),
                        rental_fee: previousFee
                          ? Math.round(previousFee * 1.1)
                          : null,
                        increase_fee_rate: previousFee ? 10 : null,
                        contract_payment_calculation_type_id: null,
                        revenue_share_percentage: null,
                        payment_file: null,
                      },
                    ]);
                  }}
                />
              )}
            </>
          )}
          {selectedTabId === tabs?.[3].id && (
            <OtherFee
              depositTypes={depositTypes}
              errorData={errorData}
              data={data}
              updateAttr={updateAttr}
              fieldIsInvalid={fieldIsInvalid}
              isViewContract={isViewContract}
            />
          )}
          {selectedTabId === tabs?.[4].id && (
            <Question
              data={data}
              errorData={errorData}
              fieldIsInvalid={fieldIsInvalid}
              isViewContract={isViewContract}
              updateAttr={updateAttr}
            />
          )}
        </div>
      </div>
      {renderFooter()}
    </Modal>
  );
};

export default ModalCreateContract;
